<template>
  <FinancialTimelineEntry
    label="Financial insights"
    v-bind="{
      title: formattedHeadline || 'Analyzing your financial health…',
      titleCallouts,
    }"
    :lastUpdatedTimestamp="payload?.SnapshotDate"
  >
    <div
      v-if="financialInsightsSnapshot"
      class="FinancialInsightsEntryView__content"
    >
      <FinancialCircleChart
        v-model="financialInsightsSnapshot"
        class="FinancialInsightsEntryView__circleChart"
        data-testid="chart"
        :class="{
          'FinancialInsightsEntryView__circleChart--isClickable': !shouldCompleteOnboarding,
        }"
        @click="!shouldCompleteOnboarding ? handleViewClick() : undefined"
      />
      <!-- eslint-disable vue/no-v-html -->
      <UIText
        class="FinancialInsightsEntryView__summary"
        data-testid="summary"
        tag="div"
        size="Medium"
        isMultiline
      >
        <div
          class="FinancialInsightsEntryView__summaryText"
          v-html="formattedSummary"
        />
      </UIText>
      <!-- eslint-enable vue/no-v-html -->

      <div class="FinancialInsightsEntryView__buttonContainer">
        <FormButton
          v-if="!shouldCompleteOnboarding"
          data-testid="viewButton"
          variant="Primary"
          isRounded
          @click="handleViewClick"
        >
          View highlights
        </FormButton>

        <FormButton
          data-testid="onboardingButton"
          :variant="shouldCompleteOnboarding ? 'Primary' : 'Secondary'"
          isRounded
          @click="handleOnboardingClick"
        >
          <template v-if="shouldCompleteOnboarding">
            Complete your profile
          </template>
          <template v-else>
            Update your profile
          </template>
        </FormButton>
      </div>

      <section
        v-if="financialInsightsSnapshot.CommonQuestions.length > 0"
        class="FinancialInsightsEntryView__section"
        data-testid="questions"
      >
        <UIText
          tag="h4"
          size="Large"
          weight="Medium"
        >
          Ask about your finances
        </UIText>

        <ul class="FinancialInsightsEntryView__questionCarousel">
          <li
            v-for="question in financialInsightsSnapshot.CommonQuestions"
            :key="question.Id"
            class="FinancialInsightsEntryView__questionCarouselItem"
            data-testid="question"
          >
            <ChatQuestionCard
              :modelValue="question"
              :isLoading="isCreatingChat && currentQuestionId === question.Id"
              @click="handleQuestionClick(question)"
            />
          </li>
        </ul>
      </section>
    </div>
  </FinancialTimelineEntry>
</template>

<script lang="ts" setup>
import FinancialTimelineEntry from "@cosine/components/FinancialTimelineEntry.vue";
import useFinancialTimelineEntry from "@cosine/composables/useFinancialTimelineEntry";
import { FinancialEntryPayloadTypeName } from "@cosine/composables/useFinancialTimelineEntry.types";
import { IClientTimelineEntry, IFinancialInsightsCommonQuestion, IFinancialInsightsSnapshot } from "@cosine/types/api-models";
import { computed, ref } from "vue";
import FormButton from "@cosine/components/FormButton.vue";
import UIText from "@cosine/components/UIText.vue";
import useModalRouter from "@cosine/composables/useModalRouter";
import { RouteName } from "@cosine/routes";
import { useRouter } from "vue-router";
import ChatQuestionCard from "@cosine/components/ChatQuestionCard.vue";
import useChat from "@cosine/composables/useChat";
import useRequest from "@cosine/composables/useRequest";
import { marked } from "marked";
import FinancialCircleChart from "@cosine/components/FinancialCircleChart.vue";
import isTruthy from "@cosine/lib/utils/boolean/isTruthy";
import stripMarkdown from "@cosine/lib/utils/string/stripMarkdown";

const modelValue = defineModel<IClientTimelineEntry>({
  required: true,
});

const {
  openModal,
} = useModalRouter();

const router = useRouter();

const {
  handleRequest,
  isRequesting: isCreatingChat,
} = useRequest();

const {
  createNewChatWithMessage,
} = useChat();

const {
  payload,
  snapshot,
} = useFinancialTimelineEntry(modelValue.value.IdReference, FinancialEntryPayloadTypeName.FinancialInsightsPayload);
const currentQuestionId = ref<string>();

const financialInsightsSnapshot = computed(() => {
  return snapshot.value as IFinancialInsightsSnapshot | undefined;
});

const shouldCompleteOnboarding = computed(() => {
  return !financialInsightsSnapshot.value || financialInsightsSnapshot.value.Meta.DataAssessment.CompletenessScore < 50;
});

const formattedHeadline = computed(() => stripMarkdown(financialInsightsSnapshot.value?.Summary.Headline));

const titleCallouts = computed(() => {
  return [financialInsightsSnapshot.value?.Summary.HeadlineEmphasis].filter(isTruthy);
});

const formattedSummary = computed(() => {
  if (!financialInsightsSnapshot.value) return;

  return marked(financialInsightsSnapshot.value.Summary.Paragraphs.join("\n\n"));
});

async function handleQuestionClick (question: IFinancialInsightsCommonQuestion) {
  if (isCreatingChat.value) return;

  currentQuestionId.value = question.Id;

  const [, id] = await handleRequest(createNewChatWithMessage(question.QuestionText), {
    showOnlyErrors: true,
  });

  if (id) {
    openModal({
      name: RouteName.threadsView,
      params: {
        id,
      },
      state: {
        shouldAutofocus: false,
      },
    });
  }
}

function handleViewClick () {
  openModal({
    name: RouteName.insightsView,
    params: {
      timelineEntryId: modelValue.value.IdReference,
    },
  });
}

function handleOnboardingClick () {
  const screenName = shouldCompleteOnboarding.value
    ? "name"
    : "employment";

  router.push({
    name: RouteName.onboarding,
    hash: `#q=${screenName}`,
  });
}
</script>

<style lang="scss" scoped>
.FinancialInsightsEntryView__summary {
  padding: 0 var(--layoutMargin) 24px;
}

.FinancialInsightsEntryView__summaryText {
  display: grid;
  gap: 16px;
}

.FinancialInsightsEntryView__section {
  display: grid;
  margin: 0 var(--layoutMargin);
  padding: 20px 0 24px;

  border-top: 1px solid var(--themeColorBorder);

  &:last-of-type {
    border-bottom: 1px solid var(--themeColorBorder);
  }
}

.FinancialInsightsEntryView__questionCarousel {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: stretch;
  margin: 20px calc(-1 * var(--layoutMargin)) 0;
  padding-inline: var(--layoutMargin);
  overflow-x: auto;

  mask: linear-gradient(
    to right,
    transparent 0,
    black var(--layoutMargin),
    black calc(100% - var(--layoutMargin)),
    transparent 100%,
  );

  scroll-padding-inline: var(--layoutMargin);
  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }
}

.FinancialInsightsEntryView__questionCarouselItem {
  display: grid;
  min-width: min(calc(100% - 2 * var(--layoutMargin)), 300px);

  scroll-snap-align: center;
  scroll-snap-stop: always;
}

.FinancialInsightsEntryView__questionCarouselItemButton {
  display: grid;
  grid: min-content auto / auto;
  gap: 16px;
  padding: 16px var(--layoutMargin) 16px;
  overflow: hidden;

  border-radius: 12px;
  background-color: var(--colorWhite);

  text-align: left;

  cursor: pointer;
}

.FinancialInsightsEntryView__buttonContainer {
  display: grid;
  gap: 12px;
  padding: 24px var(--layoutMargin);
}

.FinancialInsightsEntryView__circleChart {
  max-width: 500px;
  margin-inline: auto;
  margin-bottom: 32px;
  padding: 0 var(--layoutMargin);
}

.FinancialInsightsEntryView__circleChart--isClickable {
  cursor: pointer;
}
</style>
